import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import styles from "../styles/loopingDemos.module.css";

/* ─────────────────────────────────────────────────────────────
 * Pause animations when the element is off-screen (battery-safe).
 * ────────────────────────────────────────────────────────────*/
function useInViewport(ref, rootMargin = "150px 0px") {
  // Default: start playing when the demo is within 150px of the viewport
  // (before it's fully visible) so the user never sees a "frozen" state.
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => {
        // Once we've seen it, keep it playing to avoid restart flicker
        // on borderline scroll positions.
        if (entry.isIntersecting) setInView(true);
      },
      { rootMargin, threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, rootMargin]);
  return inView;
}

/* Shared OS-style window chrome -------------------------------- */
const DemoChrome = ({ kind, size, title, badges, children }) => (
  <div className={styles.demoFrame} data-kind={kind} data-size={size}>
    <div className={styles.demoTopBar}>
      <div className={styles.trafficLights}>
        <span style={{ background: "#FF5F57" }} />
        <span style={{ background: "#FEBC2E" }} />
        <span style={{ background: "#28C840" }} />
      </div>
      <span className={styles.demoTitle}>{title}</span>
      <div className={styles.demoBadges}>
        {badges?.map((b, i) => (
          <span key={i} className={styles.demoBadge} data-tone={b.tone}>
            {b.icon}
            {b.label}
          </span>
        ))}
      </div>
    </div>
    {children}
  </div>
);

/* ═══════════════════════════════════════════════════════════════
 *  1) WorkspaceSplitDemo
 *     Split-pane PDF + AI explainer. Cursor highlights a passage,
 *     AI streams an explanation in the right pane, then jumps to
 *     the next passage. Nothing else on the market does this.
 * ═══════════════════════════════════════════════════════════════*/
const PDF_PASSAGES = [
  {
    fullText:
      "Photosynthesis is the biochemical process by which plants, algae, and certain bacteria convert sunlight, water, and carbon dioxide into glucose and oxygen. The reaction occurs in two distinct stages: the light-dependent reactions in the thylakoid membranes, and the Calvin cycle in the stroma of the chloroplast.",
    highlightStart: 22,
    highlightEnd: 143,
    pdfTitle: "Chapter 7 — Photosynthesis",
    pageNum: "142",
    subject: "Biology",
    tag: "Midterm",
    aiTitle: "Explaining your selection",
    aiBody:
      "This passage describes photosynthesis as a two-stage reaction. Stage 1 captures light energy as ATP and NADPH; stage 2 uses that energy to fix CO₂ into glucose. The key equation is 6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂.",
    sourceLabel: "chapter7.pdf · p. 142",
    notesTitle: "Photosynthesis — Two Stages",
    notes: [
      "Two-stage biochemical reaction: light-dependent + Calvin cycle",
      "Stage 1 (thylakoid): captures light → ATP + NADPH",
      "Stage 2 (stroma): fixes CO₂ → glucose using Stage 1 energy",
      "Net equation: 6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂",
    ],
  },
  {
    fullText:
      "Mitosis is a process of cell division that results in two genetically identical daughter cells, each containing the same chromosome number as the parent cell. It proceeds through four phases: prophase, metaphase, anaphase, and telophase, followed by cytokinesis.",
    highlightStart: 0,
    highlightEnd: 158,
    pdfTitle: "Chapter 9 — Cell Division",
    pageNum: "88",
    subject: "Biology",
    tag: "Lab Quiz",
    aiTitle: "Summarizing this paragraph",
    aiBody:
      "Mitosis is the cell-cloning process. Four sequential phases (PMAT) reorganize chromosomes so each daughter cell receives an identical set. Cytokinesis then physically splits the cytoplasm.",
    sourceLabel: "biology_notes.pdf · p. 88",
    notesTitle: "Mitosis — 4 Phases (PMAT)",
    notes: [
      "Produces 2 genetically identical daughter cells",
      "Phases: Prophase → Metaphase → Anaphase → Telophase",
      "Mnemonic: PMAT — each phase rearranges chromosomes",
      "Cytokinesis follows: cytoplasm splits into 2 cells",
    ],
  },
  {
    fullText:
      "The Industrial Revolution, beginning in Britain around 1760, marked the transition from hand production methods to machines, new chemical manufacturing and iron production processes, and the increasing use of steam and water power. It fundamentally restructured labor, urbanization, and class hierarchy.",
    highlightStart: 93,
    highlightEnd: 231,
    pdfTitle: "Chapter 3 — The Industrial Era",
    pageNum: "14",
    subject: "History",
    tag: "Essay",
    aiTitle: "Connecting to your essay outline",
    aiBody:
      "This connects directly to your thesis on labor restructuring. Note the four pillars: machine production, chemical manufacturing, iron production, and steam power — each maps to a body paragraph in your draft.",
    sourceLabel: "history_lecture.pdf · p. 14",
    notesTitle: "Industrial Revolution — 4 Pillars",
    notesKicker: "→ Maps to essay body paragraphs",
    notes: [
      "Began in Britain, c. 1760",
      "Pillar 1: Mechanized production (replaces cottage industry)",
      "Pillar 2: Chemical manufacturing",
      "Pillar 3: Iron production at scale",
      "Pillar 4: Steam + water power",
    ],
  },
];

export const WorkspaceSplitDemo = () => {
  const rootRef = useRef(null);
  const inView = useInViewport(rootRef);
  const [idx, setIdx] = useState(0);
  // phases: highlight → aiStream → notesStream → hold
  const [phase, setPhase] = useState("highlight");
  const [aiText, setAiText] = useState("");
  const [highlightProgress, setHighlightProgress] = useState(0);
  const [notesRevealed, setNotesRevealed] = useState(0);

  const passage = PDF_PASSAGES[idx];

  useEffect(() => {
    if (!inView) return undefined;
    const timers = [];
    const add = (fn, ms) => timers.push(setTimeout(fn, ms));
    const clear = () => timers.forEach(clearTimeout);

    if (phase === "highlight") {
      setAiText("");
      setNotesRevealed(0);
      setHighlightProgress(0);
      let p = 0;
      const grow = () => {
        p += 0.08;
        setHighlightProgress(Math.min(1, p));
        if (p < 1) add(grow, 24);
        else add(() => setPhase("aiStream"), 380);
      };
      add(grow, 700);
    } else if (phase === "aiStream") {
      const words = passage.aiBody.split(" ");
      let i = 0;
      const stream = () => {
        setAiText(words.slice(0, i).join(" "));
        if (i < words.length) {
          i += 1;
          add(stream, 46 + Math.random() * 30);
        } else {
          add(() => setPhase("notesStream"), 700);
        }
      };
      stream();
    } else if (phase === "notesStream") {
      // Drop bullet notes in one at a time
      let n = 0;
      const next = () => {
        n += 1;
        setNotesRevealed(n);
        if (n < passage.notes.length) {
          add(next, 520);
        } else {
          add(() => setPhase("hold"), 1500);
        }
      };
      add(next, 200);
    } else if (phase === "hold") {
      add(() => {
        setIdx((s) => (s + 1) % PDF_PASSAGES.length);
        setPhase("highlight");
      }, 1200);
    }

    return clear;
  }, [phase, idx, passage, inView]);

  /* Build the highlighted passage as 3 segments.
     The growing edge snaps forward to the next word boundary so the
     highlight never cuts mid-word (e.g. "pa|rent"). */
  const pre = passage.fullText.slice(0, passage.highlightStart);
  const rawEnd =
    passage.highlightStart +
    Math.round(
      (passage.highlightEnd - passage.highlightStart) * highlightProgress,
    );
  let hlEnd = rawEnd;
  if (rawEnd > passage.highlightStart && rawEnd < passage.highlightEnd) {
    // If we're in the middle of a word, extend forward to its end.
    const between = passage.fullText.slice(rawEnd, passage.highlightEnd);
    const nextBoundary = between.search(/\s/);
    hlEnd =
      nextBoundary === -1
        ? passage.highlightEnd
        : Math.min(passage.highlightEnd, rawEnd + nextBoundary);
  }
  const hl = passage.fullText.slice(passage.highlightStart, hlEnd);
  const post = passage.fullText.slice(passage.highlightEnd);
  const remainder = passage.fullText.slice(hlEnd, passage.highlightEnd);

  const showAI = phase !== "highlight";

  /* Snippet of highlighted text shown in Lumi context chip */
  const hlSnippet = passage.fullText
    .slice(passage.highlightStart, passage.highlightEnd)
    .slice(0, 52)
    .trimEnd() + "…";

  return (
    <div ref={rootRef} className={styles.demoMount}>
      <DemoChrome
        kind="split"
        size="hero"
        title={`Workspace · ${passage.subject}`}
        badges={[
          { label: "⌘K", tone: "key" },
          {
            label: "Focus",
            tone: "live",
            icon: <span className={styles.liveDot} />,
          },
        ]}
      >
        <div className={styles.splitBody}>

          {/* ── Pane 1 — PDF viewer ── */}
          <div className={styles.splitPaneLeft}>
            <div className={styles.paneHeader}>
              <span className={styles.paneTabActive}>
                <span className={styles.pdfTabIcon}>📄</span>
                {passage.sourceLabel}
              </span>
              <span className={styles.paneTab}>+ tab</span>
            </div>
            <div className={styles.pdfBody}>
              <div className={styles.pdfPageHeader}>
                <span className={styles.pdfChapterTitle}>{passage.pdfTitle}</span>
                <span className={styles.pdfPageBadge}>p. {passage.pageNum}</span>
              </div>
              <div className={styles.pdfRule} />
              <div className={styles.pdfText}>
                {pre}
                <span className={styles.pdfHighlight}>{hl}</span>
                {phase === "highlight" && (
                  <span className={styles.pdfHighlightPending}>{remainder}</span>
                )}
                {phase !== "highlight" && (
                  <span className={styles.pdfHighlight}>{remainder}</span>
                )}
                {post}
              </div>
            </div>
            <div className={styles.paneFooter}>
              <span className={styles.paneFooterMeta}>p. {passage.pageNum} / 218</span>
              <span className={styles.paneFooterBadge}>PDF</span>
            </div>
          </div>

          {/* ── Pane 2 — Lumi AI ── */}
          <div className={styles.splitPaneMid}>
            <div className={styles.paneHeader}>
              <span className={styles.aiPaneTitle}>
                <span className={styles.aiSparkle} />
                Lumi
              </span>
              <span className={styles.paneAction}>cite</span>
            </div>
            <div className={styles.aiPaneBody}>
              {phase === "highlight" && (
                <div className={styles.lumiIdleState}>
                  <p className={styles.lumiIdleText}>
                    Select any text to explain, summarize, or connect to your notes
                  </p>
                </div>
              )}
              {showAI && (
                <>
                  <div className={styles.lumiContextChip}>
                    <span className={styles.lumiContextQuote}>"</span>
                    <span className={styles.lumiContextText}>{hlSnippet}</span>
                  </div>
                  <div className={styles.aiPaneSubject}>
                    <span className={styles.aiPaneCaret}>▸</span>
                    {passage.aiTitle}
                  </div>
                  <div className={styles.aiPaneAnswer}>
                    {aiText}
                    {phase === "aiStream" && (
                      <span className={styles.streamCaret} />
                    )}
                  </div>
                  {phase !== "aiStream" && (
                    <div className={styles.aiPaneSourceChip}>
                      <span className={styles.sourceDot} />
                      {passage.sourceLabel}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Pane 3 — Notes ── */}
          <div className={styles.splitPaneRight}>
            <div className={styles.paneHeader}>
              <span className={styles.notesPaneTitle}>
                <span className={styles.notesPencil}>✎</span>
                My Notes
              </span>
              <span className={styles.paneAction}>md</span>
            </div>
            <div className={styles.notesBody}>
              {notesRevealed === 0 && phase !== "notesStream" && phase !== "hold" && (
                <div className={styles.notesEmpty}>
                  Key points will appear here automatically
                </div>
              )}

              {(notesRevealed > 0 || phase === "notesStream" || phase === "hold") && (
                <>
                  <div className={styles.notesTagRow}>
                    <span className={styles.notesTag}>{passage.subject}</span>
                    <span className={styles.notesTagAlt}>{passage.tag}</span>
                  </div>
                  <div className={styles.notesTitle}>
                    {passage.notesTitle}
                    {passage.notesKicker && (
                      <span className={styles.notesKicker}>{passage.notesKicker}</span>
                    )}
                  </div>
                  <ul className={styles.notesList}>
                    {passage.notes.slice(0, notesRevealed).map((note, i) => (
                      <li key={`${idx}-${i}`} className={styles.notesItem}>
                        <span className={styles.notesBullet} />
                        <span className={styles.notesText}>{note}</span>
                      </li>
                    ))}
                  </ul>
                  {phase === "hold" && notesRevealed === passage.notes.length && (
                    <div className={styles.notesSavedChip}>
                      <span className={styles.sourceDot} />
                      Saved to {passage.subject} · {passage.pdfTitle.split("—")[0].trim()}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>
      </DemoChrome>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
 * MiniChatDemo — rich AI tutor widget for Card 1 footer.
 *
 * Showcases: PDF-context awareness, quoted-source input,
 * reasoning transparency, inline citations, follow-up action
 * chips, multimodal input bar. Far beyond "basic chat".
 * ────────────────────────────────────────────────────────────*/
const MINI_CHAT_SCRIPTS = [
  {
    contextDoc: "chapter7.pdf",
    contextMeta: "42p · indexed",
    quote: "thylakoid membranes",
    question: "Why is this important?",
    reasoning: "searching 3 sources · cross-referencing lecture notes",
    reasoningSources: 3,
    reply: [
      { t: "This is where the " },
      { t: "light-dependent reactions", bold: true },
      { t: " happen " },
      { cite: 1 },
      { t: " — they capture photons and produce ATP/NADPH " },
      { cite: 2 },
      { t: "." },
    ],
    chips: ["↻ Simpler", "✎ Quiz me", "＋ Save to notes"],
  },
  {
    contextDoc: "calculus_notes.pdf",
    contextMeta: "14p · indexed",
    quote: "ax² + bx + c = 0",
    question: "Solve for x?",
    reasoning: "deriving solution · checking against lecture slides",
    reasoningSources: 2,
    reply: [
      { t: "Use the quadratic formula: " },
      { formula: "x = (−b ± √(b²−4ac)) / 2a", },
      { t: " The discriminant " },
      { code: "b²−4ac" },
      { t: " tells you how many real roots exist " },
      { cite: 1 },
      { t: "." },
    ],
    chips: ["↻ Worked example", "✎ Practice set", "＋ Save to notes"],
  },
  {
    contextDoc: "biology_notes.pdf",
    contextMeta: "88p · indexed",
    quote: "prophase, metaphase, anaphase, telophase",
    question: "Explain to a 12-year-old",
    reasoning: "adjusting reading level · simplifying vocabulary",
    reasoningSources: 4,
    reply: [
      { t: "Imagine a cell " },
      { bold: true, t: "copying itself" },
      { t: ". Four stages " },
      { cite: 1 },
      { t: ": line up chromosomes, split them, pull apart, then wrap up " },
      { cite: 2 },
      { t: ". The cell divides in two 🧬" },
    ],
    chips: ["↻ Even simpler", "✎ Quiz me", "＋ Save to notes"],
  },
];

export const MiniChatDemo = () => {
  const rootRef = useRef(null);
  const inView = useInViewport(rootRef);
  const [scriptIdx, setScriptIdx] = useState(0);
  // phases: userType → thinking → aiStream → followups → hold
  const [phase, setPhase] = useState("userType");
  const [userText, setUserText] = useState("");
  const [aiSegmentsShown, setAiSegmentsShown] = useState(0);
  const [aiWordText, setAiWordText] = useState("");
  const [chipsShown, setChipsShown] = useState(0);

  const script = MINI_CHAT_SCRIPTS[scriptIdx];

  useEffect(() => {
    if (!inView) return undefined;
    const timers = [];
    const add = (fn, ms) => timers.push(setTimeout(fn, ms));
    const clear = () => timers.forEach(clearTimeout);

    if (phase === "userType") {
      setUserText("");
      setAiSegmentsShown(0);
      setAiWordText("");
      setChipsShown(0);
      let i = 0;
      const tick = () => {
        setUserText(script.question.slice(0, i));
        if (i < script.question.length) {
          i += 1;
          add(tick, 32 + Math.random() * 22);
        } else {
          add(() => setPhase("thinking"), 500);
        }
      };
      tick();
    } else if (phase === "thinking") {
      add(() => setPhase("aiStream"), 1300);
    } else if (phase === "aiStream") {
      // Stream reply segment by segment; for text segments, stream word-by-word
      let segIdx = 0;
      const advanceSegment = () => {
        if (segIdx >= script.reply.length) {
          add(() => setPhase("followups"), 500);
          return;
        }
        const seg = script.reply[segIdx];
        if (seg.t) {
          // Word-stream text
          const words = seg.t.split(" ");
          let wi = 0;
          const streamWord = () => {
            setAiWordText(words.slice(0, wi).join(" "));
            if (wi < words.length) {
              wi += 1;
              add(streamWord, 44 + Math.random() * 26);
            } else {
              setAiSegmentsShown(segIdx + 1);
              setAiWordText("");
              segIdx += 1;
              add(advanceSegment, 80);
            }
          };
          streamWord();
        } else {
          // Non-text segment (cite/code/formula) pops in whole
          setAiSegmentsShown(segIdx + 1);
          segIdx += 1;
          add(advanceSegment, 180);
        }
      };
      advanceSegment();
    } else if (phase === "followups") {
      let c = 0;
      const next = () => {
        c += 1;
        setChipsShown(c);
        if (c < script.chips.length) {
          add(next, 140);
        } else {
          add(() => setPhase("hold"), 2400);
        }
      };
      add(next, 200);
    } else if (phase === "hold") {
      add(() => {
        setScriptIdx((s) => (s + 1) % MINI_CHAT_SCRIPTS.length);
        setPhase("userType");
      }, 400);
    }
    return clear;
  }, [phase, scriptIdx, script, inView]);

  const showAi = phase === "aiStream" || phase === "followups" || phase === "hold";
  const showFollowups = phase === "followups" || phase === "hold";

  const renderSegment = (seg, i, isLastStreaming) => {
    if (seg.cite) {
      return (
        <span key={i} className={styles.miniCite}>
          [{seg.cite}]
        </span>
      );
    }
    if (seg.code) {
      return (
        <code key={i} className={styles.miniCode}>
          {seg.code}
        </code>
      );
    }
    if (seg.formula) {
      return (
        <span key={i} className={styles.miniFormula}>
          {seg.formula}
        </span>
      );
    }
    // text
    const content = isLastStreaming ? aiWordText : seg.t;
    if (seg.bold) {
      return (
        <strong key={i} className={styles.miniBold}>
          {content}
        </strong>
      );
    }
    return <span key={i}>{content}</span>;
  };

  return (
    <div ref={rootRef} className={styles.miniChatWrap}>
      {/* Context chip — shows which doc Lumi is reading from */}
      <div className={styles.miniContextBar}>
        <span className={styles.miniContextIcon}>📎</span>
        <span className={styles.miniContextDoc}>{script.contextDoc}</span>
        <span className={styles.miniContextMeta}>{script.contextMeta}</span>
        <span className={styles.miniContextStatus}>
          <span className={styles.miniStatusDot} />
          connected
        </span>
      </div>

      {/* User message with quoted source chip */}
      <div className={styles.miniChatRow} data-side="right">
        <div className={styles.miniUserWrap}>
          <div className={styles.miniQuoteChip}>
            <span className={styles.miniQuoteCaret}>▸</span>
            &ldquo;{script.quote}&rdquo;
          </div>
          <div className={styles.miniUserBubble}>
            {userText}
            {phase === "userType" && <span className={styles.caret} />}
          </div>
        </div>
      </div>

      {/* Reasoning step — transparency about what Lumi is doing */}
      {phase !== "userType" && (
        <div className={styles.miniReasoning}>
          <span className={styles.miniReasoningArrow}>↳</span>
          {phase === "thinking" ? (
            <>
              <span className={styles.miniReasoningText}>
                {script.reasoning}
              </span>
              <span className={styles.miniSpinner} />
            </>
          ) : (
            <span className={styles.miniReasoningDone}>
              <span className={styles.miniReasoningCheck}>✓</span>
              answered · {script.reasoningSources} sources
            </span>
          )}
        </div>
      )}

      {/* AI reply with rich inline content */}
      {showAi && (
        <div className={styles.miniChatRow} data-side="left">
          <div className={styles.miniAvatar}>L</div>
          <div className={styles.miniAiBubble}>
            {script.reply.slice(0, aiSegmentsShown).map((seg, i) =>
              renderSegment(seg, i, false),
            )}
            {phase === "aiStream" &&
              aiSegmentsShown < script.reply.length &&
              script.reply[aiSegmentsShown].t && (
                <>
                  {renderSegment(
                    script.reply[aiSegmentsShown],
                    aiSegmentsShown,
                    true,
                  )}
                  <span className={styles.streamCaret} />
                </>
              )}
          </div>
        </div>
      )}

      {/* Follow-up suggestion chips */}
      {showFollowups && (
        <div className={styles.miniChips}>
          {script.chips.slice(0, chipsShown).map((chip, i) => (
            <button
              key={`${scriptIdx}-${i}`}
              className={styles.miniChip}
              type="button"
              tabIndex={-1}
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* Input bar with attachment, placeholder, mic, level pill */}
      <div className={styles.miniInputBar}>
        <button
          type="button"
          className={styles.miniInputIcon}
          tabIndex={-1}
          aria-label="Attach"
        >
          📎
        </button>
        <span className={styles.miniInputPlaceholder}>
          Ask anything about {script.contextDoc}…
        </span>
        <span className={styles.miniLevelPill}>College</span>
        <button
          type="button"
          className={styles.miniInputIcon}
          tabIndex={-1}
          aria-label="Voice"
        >
          🎤
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 *  2) CommandPaletteDemo
 *     ⌘K palette where students type natural-language commands
 *     to spawn quizzes / flashcards / summaries — all without
 *     leaving the workspace. Keyboard-driven, power-user feel.
 * ═══════════════════════════════════════════════════════════════*/
const PALETTE_SCRIPTS = [
  {
    query: "/quiz chapter 7",
    options: [
      { icon: "◆", label: "Generate 12-question quiz from Chapter 7", shortcut: "↵", primary: true },
      { icon: "▭", label: "Make 20 flashcards from Chapter 7", shortcut: "⌘F" },
      { icon: "≡", label: "Summarize Chapter 7 (1-page)", shortcut: "⌘S" },
      { icon: "◐", label: "Add Chapter 7 to study session", shortcut: "⌘+" },
    ],
    result: "12 questions generated · ready in study session",
    kicker: "1.4s",
  },
  {
    query: "explain mitosis like i'm 12",
    options: [
      { icon: "✦", label: "Explain mitosis (ELI12 mode)", shortcut: "↵", primary: true },
      { icon: "▭", label: "Make analogy flashcards", shortcut: "⌘F" },
      { icon: "▷", label: "Find a 3-min video on mitosis", shortcut: "⌘V" },
      { icon: "✎", label: "Insert explanation into notes", shortcut: "⌘N" },
    ],
    result: "Saved to Biology · Cell Division",
    kicker: "0.9s",
  },
  {
    query: "/cite all sources in essay",
    options: [
      { icon: "❝", label: "Auto-cite all claims (MLA 9)", shortcut: "↵", primary: true },
      { icon: "≡", label: "Switch to APA 7 format", shortcut: "⌘A" },
      { icon: "⎘", label: "Export bibliography as .bib", shortcut: "⌘E" },
      { icon: "✓", label: "Verify each source online", shortcut: "⌘V" },
    ],
    result: "9 citations added · 3 sources verified",
    kicker: "2.1s",
  },
];

export const CommandPaletteDemo = () => {
  const rootRef = useRef(null);
  const inView = useInViewport(rootRef);
  const [scriptIdx, setScriptIdx] = useState(0);
  const [phase, setPhase] = useState("opening"); // opening | typing | selecting | running | hold
  const [query, setQuery] = useState("");
  const [selectedRow, setSelectedRow] = useState(0);

  const script = PALETTE_SCRIPTS[scriptIdx];

  useEffect(() => {
    if (!inView) return undefined;
    const timers = [];
    const add = (fn, ms) => timers.push(setTimeout(fn, ms));
    const clear = () => timers.forEach(clearTimeout);

    if (phase === "opening") {
      setQuery("");
      setSelectedRow(0);
      add(() => setPhase("typing"), 450);
    } else if (phase === "typing") {
      let i = 0;
      const tick = () => {
        setQuery(script.query.slice(0, i));
        if (i < script.query.length) {
          i += 1;
          add(tick, 38 + Math.random() * 30);
        } else {
          add(() => setPhase("selecting"), 600);
        }
      };
      tick();
    } else if (phase === "selecting") {
      // brief arrow-key dance, then settle on first result
      add(() => setSelectedRow(1), 320);
      add(() => setSelectedRow(2), 540);
      add(() => setSelectedRow(0), 780);
      add(() => setPhase("running"), 1100);
    } else if (phase === "running") {
      add(() => setPhase("hold"), 1400);
    } else if (phase === "hold") {
      add(() => {
        setScriptIdx((s) => (s + 1) % PALETTE_SCRIPTS.length);
        setPhase("opening");
      }, 1600);
    }

    return clear;
  }, [phase, scriptIdx, script, inView]);

  return (
    <div ref={rootRef} className={styles.demoMount}>
      <DemoChrome
        kind="palette"
        title="Workspace · Command Palette"
        badges={[
          { label: "⌘", tone: "key" },
          { label: "K", tone: "key" },
        ]}
      >
        <div className={styles.paletteBody}>
          {/* dim backdrop suggesting overlay */}
          <div className={styles.paletteBackdrop} aria-hidden="true">
            <div className={styles.paletteBackdropLine} style={{ width: "70%" }} />
            <div className={styles.paletteBackdropLine} style={{ width: "55%" }} />
            <div className={styles.paletteBackdropLine} style={{ width: "82%" }} />
            <div className={styles.paletteBackdropLine} style={{ width: "44%" }} />
          </div>

          <div className={styles.paletteCard}>
            <div className={styles.paletteInput}>
              <span className={styles.palettePrompt}>›</span>
              <span className={styles.paletteQuery}>
                {query}
                {phase === "typing" && <span className={styles.streamCaret} />}
              </span>
              <span className={styles.paletteShortcut}>esc</span>
            </div>
            <div className={styles.paletteResults}>
              {script.options.map((opt, i) => (
                <div
                  key={i}
                  className={`${styles.paletteRow} ${
                    selectedRow === i && phase !== "running" && phase !== "hold"
                      ? styles.paletteRowActive
                      : ""
                  } ${
                    phase === "running" && i === 0 ? styles.paletteRowFiring : ""
                  }`}
                >
                  <span className={styles.paletteIcon}>{opt.icon}</span>
                  <span className={styles.paletteLabel}>{opt.label}</span>
                  <span className={styles.paletteKey}>{opt.shortcut}</span>
                </div>
              ))}
            </div>
          </div>

          {(phase === "running" || phase === "hold") && (
            <div className={styles.paletteResultBar}>
              <span className={styles.paletteResultDot} />
              <span className={styles.paletteResultText}>{script.result}</span>
              <span className={styles.paletteResultTime}>{script.kicker}</span>
            </div>
          )}
        </div>
      </DemoChrome>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 *  3) EssayWritingDemo (v3 — comprehensive)
 *     Phases: prompt → titleType → bodyStream → revise → polish → check → hold
 *     Panels: assignment prompt bar, draft body w/ tracked changes,
 *             outline rail, live source verification, quality score
 * ═══════════════════════════════════════════════════════════════*/
const ESSAY_SCRIPTS = [
  {
    prompt: "Analyse the social and economic causes of the Industrial Revolution in Britain. Use at least 3 academic sources. MLA 9. 800–1000 words.",
    briefMeta: "MLA 9 · 800-1000 words · 3 academic sources",
    title: "The Industrial Revolution",
    subject: "History · Essay",
    segments: [
      { text: "The Industrial Revolution reshaped every layer of society between the late 18th and mid-19th centuries", cite: 1 },
      { text: ". Factories replaced cottage industries, cities absorbed rural populations, and steam power collapsed the distance between markets", cite: 2 },
      { text: ". Crucially, the revolution was not merely technological — it was a cultural rupture that redefined labor, time, and class ambition across generations", cite: 3 },
      { text: "." },
    ],
    revisions: [
      {
        original: "redefined labor, time, and class ambition",
        revised: "fundamentally redefined labor, time, and class ambition",
        note: "Stronger adverb for academic register",
        benefit: "Sharper claim without changing your meaning.",
      },
      {
        original: "across generations",
        revised: "across multiple generations",
        note: "Precision improved",
        benefit: "Avoids vague scope — reads more defensible.",
      },
    ],
    checks: [
      { label: "Argument strength", score: 94, color: "#22c55e" },
      { label: "Citation accuracy", score: 100, color: "#22c55e" },
      { label: "Academic register", score: 91, color: "#22c55e" },
    ],
    sources: [
      { tag: "[1]", name: "Hobsbawm, Industry & Empire", year: "1968", verified: true },
      { tag: "[2]", name: "Mokyr, The Enlightened Economy", year: "2009", verified: true },
      { tag: "[3]", name: "Lecture_07.pdf", year: "p. 22", verified: true },
    ],
  },
  {
    prompt: "Evaluate the effectiveness of international climate agreements since the Kyoto Protocol. APA 7. 750–900 words. Include primary and secondary sources.",
    briefMeta: "APA 7 · 750-900 words · primary + secondary sources",
    title: "Climate Policy & Carbon Accountability",
    subject: "Environmental Studies · Essay",
    segments: [
      { text: "International climate agreements since Kyoto have achieved partial but incomplete accountability for carbon emissions across signatory nations", cite: 1 },
      { text: ". While the Paris Agreement introduced nationally determined contributions, enforcement mechanisms remain largely voluntary and uneven", cite: 2 },
      { text: ". A binding, penalty-backed global framework is the necessary next step if net-zero targets are to remain credible and achievable", cite: 3 },
      { text: "." },
    ],
    revisions: [
      {
        original: "partial but incomplete",
        revised: "measurable but structurally incomplete",
        note: "More precise academic phrasing",
        benefit: "Signals evaluation, not opinion — better for grading rubrics.",
      },
    ],
    checks: [
      { label: "Argument strength", score: 96, color: "#22c55e" },
      { label: "Citation accuracy", score: 98, color: "#22c55e" },
      { label: "Academic register", score: 93, color: "#22c55e" },
    ],
    sources: [
      { tag: "[1]", name: "IPCC AR6 — Working Group III", year: "2022", verified: true },
      { tag: "[2]", name: "Falkner, R. — Global Governance", year: "2016", verified: true },
      { tag: "[3]", name: "lecture_climate.pdf", year: "p. 9", verified: true },
    ],
  },
  {
    prompt: "Discuss the psychological and neurological basis of memory consolidation during sleep. Harvard referencing. 850–1000 words.",
    briefMeta: "Harvard · 850-1000 words · evidence-based analysis",
    title: "Sleep & Memory Consolidation",
    subject: "Neuroscience · Essay",
    segments: [
      { text: "Memory consolidation during sleep is not passive storage but an active neural replay that strengthens synaptic connections formed during waking hours", cite: 1 },
      { text: ". Slow-wave sleep facilitates hippocampal-neocortical transfer while REM sleep sharpens procedural and emotional memory networks", cite: 2 },
      { text: ". These findings imply that sleep deprivation impairs learning at a structural, not merely functional, level — with measurable consequences for academic performance", cite: 3 },
      { text: "." },
    ],
    revisions: [
      {
        original: "not merely functional",
        revised: "not merely functional or temporary",
        note: "Qualifier added for precision",
        benefit: "Closes an easy counter-argument in one clause.",
      },
    ],
    checks: [
      { label: "Argument strength", score: 97, color: "#22c55e" },
      { label: "Citation accuracy", score: 100, color: "#22c55e" },
      { label: "Academic register", score: 95, color: "#22c55e" },
    ],
    sources: [
      { tag: "[1]", name: "Walker, Why We Sleep", year: "2017", verified: true },
      { tag: "[2]", name: "Stickgold & Walker, Nature Reviews", year: "2007", verified: true },
      { tag: "[3]", name: "lecture_neurosci.pdf", year: "p. 34", verified: true },
    ],
  },
];

const ESSAY_WORD_TARGETS = [
  [220, 300],
  [230, 320],
  [210, 290],
];

const ESSAY_CONTINUATION_SENTENCES = [
  "In practice, this means the argument must balance evidence with interpretation so each paragraph contributes to a coherent thesis rather than isolated facts.",
  "A high-scoring draft also anticipates counterarguments and addresses them directly, which demonstrates depth rather than surface-level summary.",
  "Where sources disagree, the strongest version explains why the disagreement exists and what assumptions shape each position.",
  "This is exactly where structured drafting helps: claims are introduced, supported, and then linked back to the central question with clear reasoning.",
  "By preserving source fidelity and improving sentence flow, the final submission sounds more confident, more human, and academically credible.",
  "The most persuasive sections are usually the ones that connect theory to concrete examples, because abstraction alone rarely convinces a reader.",
  "As the draft matures, transitions become more deliberate, moving from description toward evaluation without losing clarity.",
  "Ultimately, quality comes from consistency: precise language, defensible claims, and evidence that is cited at the moment it is used.",
];

const pickRandomInRange = (min, max) =>
  min + Math.floor(Math.random() * (max - min + 1));

const buildEssayContinuation = (seed, desiredWords = 190) => {
  const words = [];
  let idx = seed % ESSAY_CONTINUATION_SENTENCES.length;
  while (words.length < desiredWords) {
    const sentence = ESSAY_CONTINUATION_SENTENCES[idx];
    words.push(...sentence.split(" ").filter(Boolean));
    idx = (idx + 1) % ESSAY_CONTINUATION_SENTENCES.length;
  }
  return words.slice(0, desiredWords);
};

const ESSAY_PASS_STAGES = [
  { label: "Outline & structure",    checks: ["Intro hook", "Thesis placement", "Section balance"] },
  { label: "Building evidence",      checks: ["Source selection", "Quote accuracy", "Evidence density"] },
  { label: "Strengthening claims",   checks: ["Claim defensibility", "Counterarguments", "Logic chain"] },
  { label: "Prose & flow",           checks: ["Sentence variety", "Transition quality", "Paragraph cohesion"] },
  { label: "Citation audit",         checks: ["Source reliability", "Citation format", "Reference alignment"] },
  { label: "Final calibration",      checks: ["Academic tone", "Originality scan", "Rubric compliance"] },
];
const ESSAY_TOTAL_PASSES = ESSAY_PASS_STAGES.length;

export const EssayWritingDemo = () => {
  const rootRef = useRef(null);
  const essayBodyViewportRef = useRef(null);
  const essayParaLiveRef = useRef(null);
  const revisionCardRef = useRef(null);
  const draftFlowRef = useRef(null);
  const draftFlowProgressRef = useRef(null);
  const cursorRef = useRef(null);
  const inView = useInViewport(rootRef);
  const [scriptIdx, setScriptIdx] = useState(0);
  // phases: prompt → titleType → bodyStream → revise → polish → check → hold
  const [phase, setPhase] = useState("prompt");
  const [titleText, setTitleText] = useState("");
  const [bodyWordCount, setBodyWordCount] = useState(0);
  const [citationsRevealed, setCitationsRevealed] = useState(0);
  const [revisionIdx, setRevisionIdx] = useState(-1);
  const [sourcesVerified, setSourcesVerified] = useState(0);
  const [qualityScore, setQualityScore] = useState(0);
  const [checkIdx, setCheckIdx] = useState(0);
  const [targetWordCount, setTargetWordCount] = useState(560);
  const [continuationWords, setContinuationWords] = useState([]);
  const [passDisplay, setPassDisplay] = useState({ pass: 1, checkIdx: 0 });
  const [inDocMergePhase, setInDocMergePhase] = useState("idle"); // idle | pending | clicking | accepted

  const script = ESSAY_SCRIPTS[scriptIdx];
  const appliedRevisionCount =
    phase === "revise"
      ? Math.max(0, revisionIdx + 1)
      : ["polish", "check", "hold"].includes(phase)
        ? script.revisions.length
        : 0;
  const applyRevisionsToText = (text) => {
    let out = text;
    for (let i = 0; i < appliedRevisionCount; i++) {
      const rev = script.revisions[i];
      if (rev) out = out.replace(rev.original, rev.revised);
    }
    return out;
  };
  const effectiveSegments = script.segments.map((seg) => ({
    ...seg,
    text: applyRevisionsToText(seg.text),
  }));
  const segmentWordGroups = effectiveSegments.map((seg) =>
    seg.text.split(" ").filter(Boolean),
  );
  const baseWordTotal = segmentWordGroups.reduce((acc, part) => acc + part.length, 0);
  const visibleWordCount = Math.min(
    bodyWordCount,
    baseWordTotal + continuationWords.length,
  );

  // Build progressive body text with citation chips
  const mergedPhraseCandidate =
    appliedRevisionCount > 0
      ? script.revisions[Math.min(appliedRevisionCount - 1, script.revisions.length - 1)]
          ?.revised
      : null;
  // Phrase to highlight in-doc during revise phase (before it's fully merged)
  const inDocHighlightPhrase =
    phase === "revise" &&
    inDocMergePhase !== "idle" &&
    revisionIdx >= 0 &&
    revisionIdx < script.revisions.length
      ? script.revisions[revisionIdx].revised
      : null;
  let mergedPhraseVisualized = false;
  let inDocHighlightDone = false;
  let wordsConsumed = 0;
  const renderedSegments = [];
  for (let i = 0; i < script.segments.length; i++) {
    const seg = script.segments[i];
    const segWords = segmentWordGroups[i];
    const remaining = visibleWordCount - wordsConsumed;
    if (remaining <= 0) break;
    const slice = segWords.slice(0, remaining).join(" ");
    let sliceNode = slice;
    if (
      mergedPhraseCandidate &&
      !mergedPhraseVisualized &&
      ["polish", "check", "hold"].includes(phase) &&
      slice.includes(mergedPhraseCandidate)
    ) {
      const [before, after] = slice.split(mergedPhraseCandidate);
      sliceNode = (
        <>
          {before}
          <span className={styles.essayMergedInline}>{mergedPhraseCandidate}</span>
          {after}
        </>
      );
      mergedPhraseVisualized = true;
    } else if (inDocHighlightPhrase && !inDocHighlightDone && slice.includes(inDocHighlightPhrase)) {
      const splitIdx = slice.indexOf(inDocHighlightPhrase);
      const before = slice.slice(0, splitIdx);
      const after = slice.slice(splitIdx + inDocHighlightPhrase.length);
      sliceNode = (
        <>
          {before}
          <span className={styles.essayInDocHighlight} data-phase={inDocMergePhase}>
            {inDocHighlightPhrase}
          </span>
          {after}
        </>
      );
      inDocHighlightDone = true;
    }
    renderedSegments.push(
      <React.Fragment key={i}>
        {sliceNode}
        {seg.cite && remaining >= segWords.length && (
          <sup className={styles.essayCite}>[{seg.cite}]</sup>
        )}
      </React.Fragment>,
    );
    wordsConsumed += segWords.length;
  }
  const continuationVisibleCount = Math.max(0, visibleWordCount - baseWordTotal);
  const continuationText = continuationWords.slice(0, continuationVisibleCount).join(" ");

  useEffect(() => {
    if (!inView) return undefined;
    const timers = [];
    const add = (fn, ms) => timers.push(setTimeout(fn, ms));
    const clear = () => timers.forEach(clearTimeout);

    if (phase === "prompt") {
      setTitleText("");
      setBodyWordCount(0);
      setCitationsRevealed(0);
      setRevisionIdx(-1);
      setSourcesVerified(0);
      setQualityScore(0);
      setCheckIdx(0);
      setPassDisplay({ pass: 1, checkIdx: 0 });
      setInDocMergePhase("idle");
      const [minWords, maxWords] = ESSAY_WORD_TARGETS[scriptIdx] || [540, 700];
      const seededTarget = pickRandomInRange(minWords, maxWords);
      setTargetWordCount(seededTarget);
      const continuationNeeded = Math.max(160, seededTarget - baseWordTotal + 48);
      setContinuationWords(
        buildEssayContinuation(scriptIdx + Date.now(), continuationNeeded),
      );
      add(() => setPhase("titleType"), 980);
    } else if (phase === "titleType") {
      let i = 0;
      const tick = () => {
        setTitleText(script.title.slice(0, i));
        if (i < script.title.length) {
          i += 1;
          add(tick, 38 + Math.random() * 20);
        } else {
          add(() => setPhase("bodyStream"), 520);
        }
      };
      tick();
    } else if (phase === "bodyStream") {
      /* Typed stream: word batches + punctuation pauses, with smooth GSAP follow. */
      let kickRaf = 0;
      let lastEmitted = -1;
      let streamTimer = null;
      let i = 0;

      const startStream = () => {
        if (kickRaf) {
          cancelAnimationFrame(kickRaf);
          kickRaf = 0;
        }
        const outer = essayBodyViewportRef.current;
        const inner = essayParaLiveRef.current;
        if (!outer || !inner) {
          kickRaf = requestAnimationFrame(startStream);
          return;
        }
        const yTo = gsap.quickTo(inner, "y", { duration: 0.34, ease: "power2.out" });
        const followDraft = () => {
          const max = Math.max(0, inner.scrollHeight - outer.clientHeight);
          yTo(-max);
        };
        const tick = () => {
          if (i >= targetWordCount) {
            setBodyWordCount(targetWordCount);
            setCitationsRevealed(3);
            followDraft();
            add(() => setPhase("revise"), 420);
            return;
          }
          const remaining = targetWordCount - i;
          const batch = remaining > 130 ? 4 : remaining > 55 ? 3 : 2;
          i = Math.min(targetWordCount, i + batch);
          if (i !== lastEmitted) {
            lastEmitted = i;
            setBodyWordCount(i);
            const progress = Math.min(1, i / Math.max(targetWordCount, 1));
            if (progress >= 0.22) setCitationsRevealed(1);
            if (progress >= 0.5) setCitationsRevealed(2);
            if (progress >= 0.75) setCitationsRevealed(3);
            followDraft();
          }
          const trailingWord = continuationWords[i - 1] || "";
          const punctuationPause = /[.!?]$/.test(trailingWord)
            ? 95
            : /[,;:]$/.test(trailingWord)
              ? 55
              : 0;
          const base = batch >= 4 ? 44 : batch >= 3 ? 58 : 74;
          const jitter = Math.random() * 16;
          streamTimer = window.setTimeout(tick, base + jitter + punctuationPause);
        };
        tick();
      };
      startStream();
      return () => {
        clear();
        if (kickRaf) cancelAnimationFrame(kickRaf);
        if (streamTimer) window.clearTimeout(streamTimer);
        const live = essayParaLiveRef.current;
        if (live) {
          gsap.killTweensOf(live);
          gsap.set(live, { clearProps: "transform" });
        }
      };
    } else if (phase === "revise") {
      setInDocMergePhase("idle");
      let r = 0;
      const next = () => {
        setRevisionIdx(r);
        // show the suggested phrase highlighted in doc
        add(() => setInDocMergePhase("pending"), 380);
        // cursor animates + clicks Accept
        add(() => setInDocMergePhase("clicking"), 1300);
        // phrase transitions to accepted state in doc
        add(() => setInDocMergePhase("accepted"), 1680);
        r += 1;
        if (r < script.revisions.length) {
          add(() => setInDocMergePhase("idle"), 2500);
          add(next, 2650);
        } else {
          add(() => {
            setInDocMergePhase("idle");
            setPhase("polish");
          }, 2500);
        }
      };
      add(next, 420);
    } else if (phase === "polish") {
      add(() => setPhase("check"), 1850);
    } else if (phase === "check") {
      // verify sources one by one, then score
      let sv = 0;
      const verifySrc = () => {
        sv += 1;
        setSourcesVerified(sv);
        if (sv < script.sources.length) {
          add(verifySrc, 460);
        } else {
          // animate quality score up
          let q = 60;
          const tick = () => {
            q += 3;
            setQualityScore(Math.min(q, 96));
            if (q < 96) add(tick, 42);
            else {
              setCheckIdx(1);
              add(() => { setCheckIdx(2); }, 380);
              add(() => setPhase("hold"), 2000);
            }
          };
          add(tick, 260);
        }
      };
      add(verifySrc, 360);
    } else if (phase === "hold") {
      add(() => {
        setScriptIdx((s) => (s + 1) % ESSAY_SCRIPTS.length);
        setPhase("prompt");
      }, 1400);
    }

    return () => {
      clear();
    };
  }, [phase, scriptIdx, inView, targetWordCount, script.sources.length]);

  const showRevisionSuggestion = ["revise", "polish", "check", "hold"].includes(phase);

  // Independent timer: advance through each pass's checks, then move to next pass
  useEffect(() => {
    if (!["bodyStream", "revise", "polish"].includes(phase)) {
      if (["check", "hold"].includes(phase)) {
        const lastStage = ESSAY_PASS_STAGES[ESSAY_TOTAL_PASSES - 1];
        setPassDisplay({ pass: ESSAY_TOTAL_PASSES, checkIdx: lastStage.checks.length - 1 });
      }
      return undefined;
    }
    const ms = phase === "bodyStream" ? 1500 : 1200;
    const id = window.setInterval(() => {
      setPassDisplay((prev) => {
        const stage = ESSAY_PASS_STAGES[prev.pass - 1] || ESSAY_PASS_STAGES[0];
        const nextCheckIdx = prev.checkIdx + 1;
        if (nextCheckIdx >= stage.checks.length) {
          const nextPass = Math.min(prev.pass + 1, ESSAY_TOTAL_PASSES);
          if (nextPass === prev.pass) return prev; // already at max
          return { pass: nextPass, checkIdx: 0 };
        }
        return { pass: prev.pass, checkIdx: nextCheckIdx };
      });
    }, ms);
    return () => window.clearInterval(id);
  }, [phase, scriptIdx]);

  // GSAP cursor click animation
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor || inDocMergePhase !== "clicking") return;
    gsap.fromTo(
      cursor,
      { scale: 1, y: 0 },
      { scale: 0.78, y: 2, duration: 0.11, yoyo: true, repeat: 1, ease: "power3.in" },
    );
  }, [inDocMergePhase]);

  useEffect(() => {
    const card = revisionCardRef.current;
    if (!card) return;

    gsap.killTweensOf(card);
    if (!showRevisionSuggestion) {
      gsap.set(card, { autoAlpha: 0, y: 8, scale: 0.99 });
      return;
    }

    gsap.fromTo(
      card,
      { autoAlpha: 0, y: 10, scale: 0.985 },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.42,
        ease: "power2.out",
      },
    );
  }, [showRevisionSuggestion, scriptIdx, phase]);

  const wordCount = Math.min(bodyWordCount, targetWordCount);
  const readMin = Math.max(1, Math.ceil(wordCount / 220));

  const outlineStates = {
    prompt:     ["pending", "pending", "pending"],
    titleType:  ["active",  "pending", "pending"],
    bodyStream: ["done",    "active",  "pending"],
    revise:     ["done",    "done",    "active"],
    polish:     ["done",    "done",    "active"],
    check:      ["done",    "done",    "done"],
    hold:       ["done",    "done",    "done"],
  };
  const [introS, bodyS, conclS] = outlineStates[phase] || outlineStates.prompt;
  const fullBodyText = `${effectiveSegments.map((seg) => seg.text).join(" ")} ${continuationWords.join(" ")}`;

  const activeRevision = revisionIdx >= 0 && revisionIdx < script.revisions.length
    ? script.revisions[revisionIdx] : null;
  const displayedRevision =
    activeRevision ||
    (appliedRevisionCount > 0 ? script.revisions[appliedRevisionCount - 1] : null);
  const revisionApplied = appliedRevisionCount > 0 && phase !== "revise";
  const statusHeadline = {
    prompt: "Reading your assignment brief...",
    titleType: "Generating title from prompt...",
    bodyStream: "Drafting from your sources...",
    revise: "Lumi is refining tone and clarity...",
    polish: "Grammar check + citation formatting...",
    check: `Verifying sources... (${sourcesVerified}/${script.sources.length})`,
    hold: `Draft complete · Quality score: ${qualityScore}/100 ✓`,
  }[phase];
  const statusSubline = {
    prompt: "You can still edit the prompt while Lumi prepares the draft.",
    titleType: "Built to sound natural, specific, and human.",
    bodyStream: "Every claim stays grounded in your uploaded materials.",
    revise: "Suggestions keep your voice while improving structure.",
    polish: "Final pass improves flow without over-writing your style.",
    check: "Live validation keeps citations trustworthy.",
    hold: "Ready to submit or personalize before final export.",
  }[phase];
  const showQualityPanel = phase === "check" || phase === "hold";
  const wordGoalPct = Math.min(100, Math.round((wordCount / Math.max(targetWordCount, 1)) * 100));
  const draftFlowPass = passDisplay.pass;
  const activeFlowCheckIndex = passDisplay.checkIdx;
  const currentPassStage = ESSAY_PASS_STAGES[draftFlowPass - 1] || ESSAY_PASS_STAGES[0];
  const visibleFlowChecks = currentPassStage.checks.map((label, idx) => ({
    label,
    state:
      idx < activeFlowCheckIndex ? "done" : idx === activeFlowCheckIndex ? "active" : "queued",
  }));
  const totalChecks = ESSAY_PASS_STAGES.reduce((s, p) => s + p.checks.length, 0);
  const completedChecks =
    ESSAY_PASS_STAGES.slice(0, draftFlowPass - 1).reduce((s, p) => s + p.checks.length, 0) +
    activeFlowCheckIndex + 1;
  const flowCompletionPct = Math.min(100, Math.round((completedChecks / totalChecks) * 100));
  const activeFlowCheckLabel = currentPassStage.label;
  const originalityLabel =
    phase === "check" || phase === "hold"
      ? "Distinct from training boilerplate"
      : phase === "polish"
        ? "Checking overlap patterns…"
        : "—";

  useEffect(() => {
    if (draftFlowProgressRef.current) {
      gsap.to(draftFlowProgressRef.current, {
        width: `${flowCompletionPct}%`,
        duration: 0.45,
        ease: "power2.out",
      });
    }
    if (draftFlowRef.current) {
      const activeEl = draftFlowRef.current.querySelector(
        `.${styles.essayDraftFlowChipActive}`,
      );
      if (activeEl) {
        gsap.fromTo(
          activeEl,
          { clipPath: "inset(0 100% 0 0)", opacity: 0.65 },
          {
            clipPath: "inset(0 0% 0 0)",
            opacity: 1,
            duration: 0.34,
            ease: "power2.out",
          },
        );
      }
    }
  }, [flowCompletionPct, activeFlowCheckIndex, draftFlowPass]);

  return (
    <div ref={rootRef} className={styles.demoMount}>
      <DemoChrome
        kind="essay"
        size="hero"
        title="Workspace · Essay Draft"
        badges={[
          { label: script.subject, tone: "meta" },
          { label: `${wordCount}w`, tone: "meta" },
          { label: "Saved", tone: "live", icon: <span className={styles.liveDot} /> },
        ]}
      >
        {/* Assignment prompt bar */}
        <div className={styles.essayPromptBar}>
          <span className={styles.essayPromptIcon} aria-hidden="true">
            BR
          </span>
          <div className={styles.essayPromptContent}>
            <span className={styles.essayPromptLabel}>Assignment brief</span>
            <span className={styles.essayPromptText}>{script.prompt}</span>
            <span className={styles.essayPromptMeta}>{script.briefMeta}</span>
          </div>
        </div>

        <div className={styles.essayLayout}>
          <div className={styles.essayMain}>
            <div className={styles.essayMeta}>
              <span>Draft {scriptIdx + 1} of {ESSAY_SCRIPTS.length}</span>
              <span className={styles.essayMetaDivider} />
              <span>~{readMin} min read</span>
              <span className={styles.essayMetaDivider} />
              <span>Auto-saved</span>
            </div>
            <h3 className={styles.essayTitle}>
              {titleText}
              {phase === "titleType" && <span className={styles.caret} />}
            </h3>
            <div className={styles.essayDraftToolbar} aria-hidden="true">
              <div className={styles.essayToolbarLeft}>
                <span className={styles.essayToolbarChip}>Academic</span>
                <span className={styles.essayToolbarChip}>Source-grounded</span>
                <span className={styles.essayToolbarChipMuted}>
                  Citations {citationsRevealed}/{script.sources.length}
                </span>
              </div>
              <div className={styles.essayToolbarRight}>
                <span className={styles.essayToolbarGoalLabel}>
                  {wordCount} / {targetWordCount} words
                </span>
                <div className={styles.essayToolbarGoalTrack}>
                  <div
                    className={styles.essayToolbarGoalFill}
                    style={{ width: `${wordGoalPct}%` }}
                  />
                </div>
              </div>
            </div>
            <p className={styles.essayPara}>
              <span className={styles.essayParaGhost}>{fullBodyText}</span>
              <span
                className={`${styles.essayParaLiveViewport} ${
                  phase === "bodyStream" ? styles.essayBodyStreaming : ""
                }`}
                ref={essayBodyViewportRef}
              >
                <span className={styles.essayParaLive} ref={essayParaLiveRef}>
                  {renderedSegments}
                  {continuationText ? ` ${continuationText}` : ""}
                  {phase === "bodyStream" && <span className={styles.streamCaret} />}
                </span>
              </span>
            </p>
            <div className={styles.essayDraftFlow} aria-hidden="true">
              <div className={styles.essayDraftFlowHead}>
                <span className={styles.essayDraftFlowPass}>
                  Pass {draftFlowPass} / {ESSAY_TOTAL_PASSES}
                </span>
                <span className={styles.essayDraftFlowActiveLabel}>
                  {activeFlowCheckLabel || "Pipeline initializing"}
                </span>
                <span className={styles.essayDraftFlowPct}>{flowCompletionPct}%</span>
              </div>
              <div className={styles.essayDraftFlowTrack}>
                <div
                  className={styles.essayDraftFlowTrackFill}
                  ref={draftFlowProgressRef}
                />
              </div>
              <div className={styles.essayDraftFlowGrid} ref={draftFlowRef}>
                {visibleFlowChecks.map(
                  ({ label, state }) => (
                    <span
                      key={label}
                      className={`${styles.essayDraftFlowChip} ${
                        state === "active"
                          ? styles.essayDraftFlowChipActive
                          : state === "done"
                            ? styles.essayDraftFlowChipDone
                            : styles.essayDraftFlowChipQueued
                      }`}
                    >
                      <span className={styles.essayDraftFlowChipDot} />
                      {label}
                    </span>
                  ),
                )}
              </div>
            </div>

            {/* Keep this mounted to prevent layout jumps between phases */}
            <div className={styles.essayRevisionSlot}>
              <div
                ref={revisionCardRef}
                className={`${styles.essayRevisionCard} ${
                  revisionApplied
                    ? styles.essayRevisionCardApplied
                    : activeRevision
                      ? styles.essayRevisionCardActive
                      : styles.essayRevisionCardIdle
                } ${
                  !showRevisionSuggestion ? styles.essayRevisionCardHidden : ""
                }`}
                aria-hidden={!showRevisionSuggestion}
              >
                <span className={styles.essayRevisionLabel}>
                  {revisionApplied
                    ? "Lumi suggestion · Merged"
                    : "Lumi suggestion"}
                </span>
                <div className={styles.essayRevisionBody}>
                  <span
                    className={`${styles.essayRevisionOld} ${
                      revisionApplied ? styles.essayRevisionOldRetired : ""
                    }`}
                  >
                    {displayedRevision?.original || "Original phrase"}
                  </span>
                  <span className={styles.essayRevisionArrow}>→</span>
                  <span className={styles.essayRevisionNew}>
                    {displayedRevision?.revised || "Improved phrase"}
                  </span>
                </div>
                <span className={styles.essayRevisionNote}>
                  {displayedRevision?.note ||
                    "Context-aware revision appears here while drafting."}
                </span>
                {revisionApplied && displayedRevision?.benefit && (
                  <span className={styles.essayRevisionBenefit}>
                    {displayedRevision.benefit}
                  </span>
                )}
                <div className={styles.essayRevisionActions}>
                  {revisionApplied ? (
                    <button className={styles.essayRevisionAppliedPill} type="button">
                      Merged into draft
                    </button>
                  ) : (
                    <>
                      <span className={styles.essayAcceptBtnWrap}>
                        <button className={styles.essayRevisionAccept} type="button">
                          Accept
                        </button>
                        {(inDocMergePhase === "pending" || inDocMergePhase === "clicking") && (
                          <span
                            ref={cursorRef}
                            className={`${styles.essayAcceptCursor}${inDocMergePhase === "clicking" ? ` ${styles.essayAcceptCursorClick}` : ""}`}
                          >
                            <svg width="13" height="15" viewBox="0 0 13 15" fill="none">
                              <path
                                d="M1 1L1 13L4.2 10.2L6.1 14.2L7.8 13.5L5.8 9.5H10.5L1 1Z"
                                fill="rgba(255,255,255,0.96)"
                                stroke="rgba(0,0,0,0.28)"
                                strokeWidth="0.65"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        )}
                      </span>
                      <button className={styles.essayRevisionSkip} type="button">
                        Skip
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div
              className={`${styles.essayStatus} ${
                phase === "polish" ? styles.essayStatusPolish : ""
              } ${phase === "bodyStream" ? styles.essayStatusWriting : ""} ${
                phase === "check" ? styles.essayStatusCheck : ""
              }`}
            >
              <span className={styles.essayStatusDot} />
              <span className={styles.essayStatusText}>{statusHeadline}</span>
            </div>
            <div className={styles.essayStatusSubtext}>{statusSubline}</div>
            {(phase === "polish" || phase === "check") && <div className={styles.polishSweep} />}
          </div>

          {/* Rail: outline + sources + quality score */}
          <div className={styles.essayRail}>
            <div className={styles.essayRailSection}>
              <div className={styles.essayRailHeader}>
                Outline
                <span className={styles.essayRailHeaderCount}>3</span>
              </div>
              <div className={styles.essayOutline}>
                {[
                  { label: "Introduction", state: introS },
                  { label: "Body & Analysis", state: bodyS },
                  { label: "Conclusion", state: conclS },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`${styles.essayOutlineItem} ${
                      item.state === "active" ? styles.essayOutlineItemActive : ""
                    } ${item.state === "done" ? styles.essayOutlineItemDone : ""}`}
                  >
                    <span className={styles.essayOutlineDot} />
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.essayRailSection}>
              <div className={styles.essayRailHeader}>
                Sources
                <span className={styles.essayRailHeaderCount}>
                  {citationsRevealed}/{script.sources.length}
                </span>
              </div>
              <div className={styles.essayRailList}>
                {script.sources.map((s, i) => (
                  <div
                    key={i}
                    className={`${styles.essaySourceRow} ${
                      i < citationsRevealed ? styles.essaySourceRowActive : ""
                    }`}
                  >
                    <span className={styles.essaySourceTag}>{s.tag}</span>
                    <span className={styles.essaySourceName}>{s.name}</span>
                    <span className={styles.essaySourceYear}>
                      {i < sourcesVerified ? "✓" : s.year}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.essayRailSection}>
              <div className={styles.essayRailHeader}>
                Originality
                <span className={styles.essayRailHeaderCount}>
                  {phase === "check" || phase === "hold" ? "OK" : "—"}
                </span>
              </div>
              <p className={styles.essayRailMicro}>{originalityLabel}</p>
            </div>

            <div
              className={`${styles.essayRailSection} ${
                showQualityPanel ? styles.essayQualitySectionActive : styles.essayQualitySectionIdle
              }`}
            >
              <div className={styles.essayRailHeader}>
                Quality
                <span className={styles.essayRailHeaderCount}>
                  {showQualityPanel ? `${qualityScore}/100` : "--/100"}
                </span>
              </div>
              <div className={styles.essayQualityBar}>
                <div
                  className={styles.essayQualityFill}
                  style={{ width: `${showQualityPanel ? qualityScore : 0}%` }}
                />
              </div>
              {script.checks.map((c, i) => (
                <div
                  key={i}
                  className={`${styles.essayCheckItem} ${
                    i <= checkIdx && showQualityPanel ? styles.essayCheckItemVisible : ""
                  }`}
                >
                  <span className={styles.essayCheckDot} style={{ background: c.color }} />
                  <span className={styles.essayCheckLabel}>{c.label}</span>
                  <span className={styles.essayCheckScore}>{c.score}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer bar */}
        <div className={styles.essayFooterBar}>
          <span className={styles.essayFooterStat}><strong>{wordCount}</strong> words</span>
          <span className={styles.essayFooterDot} />
          <span className={styles.essayFooterStat}>~<strong>{readMin}</strong> min read</span>
          <span className={styles.essayFooterDot} />
          <span className={styles.essayFooterStat}><strong>{citationsRevealed}</strong> citations</span>
          {qualityScore > 0 && (
            <>
              <span className={styles.essayFooterDot} />
              <span className={styles.essayFooterStat} style={{ color: "#86efac" }}>
                <strong>{qualityScore}</strong>/100
              </span>
            </>
          )}
          <span className={styles.essayFooterSpacer} />
          <span className={styles.essayFooterModel}>
            <span className={styles.essayFooterModelDot} />
            Lumi · Academic
          </span>
        </div>
      </DemoChrome>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
 *  4) FocusSpotifyDemo
 *     Spotify focus widget integrated INTO the workspace.
 *     Animated equalizer, advancing progress bar, auto track changes,
 *     focus session timer ticks up. Hints at upcoming integration.
 * ═══════════════════════════════════════════════════════════════*/
const FOCUS_TRACKS = [
  {
    title: "Deep Focus Lo-fi",
    artist: "Lofi Girl · Chillhop",
    duration: 234,
    cover: ["#a78bfa", "#6366f1"],
  },
  {
    title: "Study With Me — Pomodoro 50/10",
    artist: "Focus Sessions",
    duration: 312,
    cover: ["#34d399", "#0ea5e9"],
  },
  {
    title: "Classical for Concentration",
    artist: "Bach, Debussy, Satie",
    duration: 287,
    cover: ["#f97316", "#f59e0b"],
  },
  {
    title: "Brown Noise — 1 hour",
    artist: "Ambient Focus",
    duration: 196,
    cover: ["#94a3b8", "#475569"],
  },
];

const formatTime = (sec) => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const FocusSpotifyDemo = () => {
  const rootRef = useRef(null);
  const inView = useInViewport(rootRef);
  const [trackIdx, setTrackIdx] = useState(0);
  const [position, setPosition] = useState(34); // start mid-track for visual interest
  const [sessionMin, setSessionMin] = useState(47);
  const [sessionSec, setSessionSec] = useState(32);

  const track = FOCUS_TRACKS[trackIdx];

  /* Playback timer — advances visually faster than realtime so the
     loop stays interesting. About 12s per "minute" of fake playback. */
  useEffect(() => {
    if (!inView) return undefined;
    const id = setInterval(() => {
      setPosition((p) => {
        const next = p + 5; // 5 seconds of music every 250ms
        if (next >= track.duration) {
          // advance to next track
          setTrackIdx((t) => (t + 1) % FOCUS_TRACKS.length);
          return 12;
        }
        return next;
      });
      // tick the focus session timer (1 real second of "studied time")
      setSessionSec((s) => {
        if (s >= 59) {
          setSessionMin((m) => m + 1);
          return 0;
        }
        return s + 1;
      });
    }, 250);
    return () => clearInterval(id);
  }, [inView, track.duration]);

  const progressPct = (position / track.duration) * 100;

  return (
    <div ref={rootRef} className={styles.demoMount}>
      <DemoChrome
        kind="focus"
        title="Workspace · Focus Session"
        badges={[
          { label: "Spotify", tone: "spotify", icon: <span className={styles.spotifyDot} /> },
          { label: "Soon", tone: "soon" },
        ]}
      >
        <div className={styles.focusBody}>
          <div className={styles.focusNowPlaying}>
            <div
              className={styles.focusCover}
              style={{
                background: `linear-gradient(135deg, ${track.cover[0]} 0%, ${track.cover[1]} 100%)`,
              }}
            >
              <div className={styles.focusEq} aria-hidden="true">
                <span /><span /><span /><span /><span />
              </div>
            </div>
            <div className={styles.focusMeta}>
              <div className={styles.focusTrack}>{track.title}</div>
              <div className={styles.focusArtist}>{track.artist}</div>
              <div className={styles.focusProgress}>
                <span className={styles.focusTime}>{formatTime(position)}</span>
                <span className={styles.focusBar}>
                  <span
                    className={styles.focusBarFill}
                    style={{ width: `${progressPct}%` }}
                  />
                  <span
                    className={styles.focusBarThumb}
                    style={{ left: `${progressPct}%` }}
                  />
                </span>
                <span className={styles.focusTime}>
                  {formatTime(track.duration)}
                </span>
              </div>
              <div className={styles.focusControls}>
                <button className={styles.focusCtrl} aria-label="Previous">⏮</button>
                <button
                  className={`${styles.focusCtrl} ${styles.focusCtrlPlay}`}
                  aria-label="Pause"
                >
                  ❚❚
                </button>
                <button className={styles.focusCtrl} aria-label="Next">⏭</button>
                <span className={styles.focusDivider} />
                <button className={styles.focusCtrl} aria-label="Shuffle">⇄</button>
                <button className={styles.focusCtrl} aria-label="Repeat">↻</button>
              </div>
            </div>
          </div>

          <div className={styles.focusUpNext}>
            <div className={styles.focusUpNextHeader}>Up next · Focus queue</div>
            {FOCUS_TRACKS.filter((_, i) => i !== trackIdx)
              .slice(0, 3)
              .map((t, i) => (
                <div key={i} className={styles.focusQueueRow}>
                  <span
                    className={styles.focusQueueDot}
                    style={{ background: t.cover[0] }}
                  />
                  <span className={styles.focusQueueTitle}>{t.title}</span>
                  <span className={styles.focusQueueDur}>
                    {formatTime(t.duration)}
                  </span>
                </div>
              ))}
          </div>

          <div className={styles.focusFooter}>
            <span className={styles.focusFooterIcon}>◐</span>
            <span className={styles.focusFooterText}>
              Today · <strong>{sessionMin}:{sessionSec.toString().padStart(2, "0")}</strong> focused
            </span>
            <span className={styles.focusFooterDivider} />
            <span className={styles.focusFooterText}>4 sessions</span>
            <span className={styles.focusFooterDivider} />
            <span className={styles.focusFooterShortcut}>
              <kbd>⌘</kbd> <kbd>P</kbd> to play
            </span>
          </div>
        </div>
      </DemoChrome>
    </div>
  );
};

